import { objectWithoutProperties as _objectWithoutProperties, objectSpread2 as _objectSpread2, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React__default__default, { useRef, useState, useLayoutEffect } from 'react';
import PropTypes__default from 'prop-types';
import styles from '../../node_modules/@flixbus/honeycomb/dist/scss/components/range.scss.js';
import HoneyBem from '../../packages/honey-bem/honey-bem.js';
import { getPercentage, remainingPercentage, isMaxedOut } from './range-calculations.js';
import { InputInfo, InputInfoChildren } from '../_atoms/input-info/input-info.js';
import RangeInput from './quarks/range-input.js';
import RangeSteps from './quarks/range-steps.js';
import { Injecss } from '../../packages/injecss/injecss.js';

const _excluded = ["disabled", "displayValues", "extraClasses", "format", "id", "info", "label", "max", "min", "rangeEnd", "step", "showSteps", "small"];
const bem = HoneyBem.init('range', styles);
const Range = _ref => {
  let {
      disabled,
      displayValues,
      extraClasses,
      format,
      id,
      info,
      label,
      max,
      min,
      rangeEnd,
      step,
      showSteps,
      small
    } = _ref,
    rest = _objectWithoutProperties(_ref, _excluded);
  // Range inputs refs
  const rangeStartRef = useRef();
  const rangeEndRef = useRef();

  // Range track ref and state
  const trackRangeRef = useRef();
  const [trackRangeStyle, setTrackRangeStyle] = useState({
    left: '0%',
    right: '0%'
  });

  // zIndex to start range when both are maxed
  // required to avoid blocking both at max value
  const [rangeStyle, setRangeStyle] = useState({});

  // Display values refs and sate
  const [displayValueStart, setDisplayValueStart] = useState(_objectSpread2({}, rest).value || _objectSpread2({}, rest).defaultValue || 0);
  const [displayValueEnd, setDisplayValueEnd] = useState(rangeEnd && (rangeEnd.value || rangeEnd.defaultValue || 0));

  // Update range track left and right styles based on input values
  const updateRangeTrack = () => {
    const newTrackRangeStyle = _objectSpread2({}, trackRangeStyle);
    if (trackRangeRef.current) {
      const trackStartDistance = getPercentage(min, max, rangeStartRef.current.value);
      if (rangeEnd) {
        const trackEndDistance = remainingPercentage(getPercentage(min, max, rangeEndRef.current.value));
        newTrackRangeStyle.left = "".concat(trackStartDistance, "%");
        newTrackRangeStyle.right = "".concat(trackEndDistance, "%");
      } else {
        newTrackRangeStyle.right = "".concat(remainingPercentage(trackStartDistance), "%");
      }
    }
    setTrackRangeStyle(newTrackRangeStyle);
  };

  // Update the display values if they have to be shown
  const updateDisplayValues = () => {
    if (displayValues) {
      if (rangeStartRef.current) {
        setDisplayValueStart(format(parseInt(rangeStartRef.current.value, 10)));
      }
      if (rangeEnd && rangeEndRef.current) {
        setDisplayValueEnd(format(parseInt(rangeEndRef.current.value, 10)));
      }
    }
  };
  useLayoutEffect(() => {
    updateRangeTrack();
    updateDisplayValues();
  }, [displayValues, rangeStartRef, rangeEndRef]);

  // Handles range inputs onChange
  const handleOnChange = (event, compareWithRef, onChangeCallback) => {
    const startValue = rangeStartRef.current && parseInt(rangeStartRef.current.value, 10);
    const endValue = rangeEndRef.current && parseInt(rangeEndRef.current.value, 10);

    // if the two ranges overlap, rewrite event value so it doesn't update
    if (startValue >= endValue) {
      // eslint-disable-next-line no-param-reassign
      event.target.value = compareWithRef.current.value;
    }
    updateDisplayValues();
    updateRangeTrack();

    // on static CSS both range inputs have z-index: 5
    // set start range z-index: 6 when both are maxed
    // required to avoid blocking both sliders at max value
    if (isMaxedOut(startValue, endValue, max)) {
      setRangeStyle({
        zIndex: 6
      });
    } else {
      setRangeStyle({});
    }
    if (typeof onChangeCallback === 'function') {
      onChangeCallback(event);
    }
  };
  const commonInputProps = {
    min,
    max,
    step,
    disabled,
    infoId: info ? "info-".concat(id) : null,
    listId: showSteps ? "".concat(id, "-steps-list") : null
  };
  return /*#__PURE__*/React__default__default.createElement("div", {
    className: bem.b({
      disabled,
      double: rangeEnd,
      small
    }, extraClasses)
  }, label && /*#__PURE__*/React__default__default.createElement("label", {
    htmlFor: id,
    className: bem.e('label')
  }, label), displayValues && /*#__PURE__*/React__default__default.createElement("span", {
    id: "displayValueStart-".concat(id),
    className: bem.e('value', 'min')
  }, displayValueStart), displayValues && rangeEnd && /*#__PURE__*/React__default__default.createElement("span", {
    id: "displayValueEnd-".concat(id),
    className: bem.e('value', 'max')
  }, displayValueEnd), /*#__PURE__*/React__default__default.createElement("div", {
    className: bem.e('field')
  }, /*#__PURE__*/React__default__default.createElement(RangeInput, _extends({
    ref: rangeStartRef,
    id: id,
    displayValueId: displayValues ? "displayValueStart-".concat(id) : null,
    style: rangeStyle
  }, commonInputProps, rest, {
    onChange: event => handleOnChange(event, rangeEndRef, _objectSpread2({}, rest).onChange)
  })), rangeEnd && /*#__PURE__*/React__default__default.createElement(RangeInput, _extends({
    ref: rangeEndRef,
    id: "".concat(id, "-max"),
    displayValueId: displayValues ? "displayValueEnd-".concat(id) : null
  }, commonInputProps, rangeEnd, {
    onChange: event => handleOnChange(event, rangeStartRef, _objectSpread2({}, rangeEnd).onChange)
  })), showSteps && /*#__PURE__*/React__default__default.createElement(RangeSteps, {
    id: "".concat(id, "-steps-list"),
    min: min,
    max: max,
    step: step,
    value: rangeStartRef.current && rangeStartRef.current.value,
    endValue: rangeEndRef.current && rangeEndRef.current.value
  }), /*#__PURE__*/React__default__default.createElement("div", {
    ref: trackRangeRef,
    className: bem.e('track-range'),
    style: trackRangeStyle
  })), info && /*#__PURE__*/React__default__default.createElement(InputInfo, {
    id: id,
    className: bem.e('info')
  }, info));
};
Range.displayName = 'Range';
Range.defaultProps = {
  disabled: false,
  displayValues: false,
  extraClasses: '',
  format: val => val,
  info: null,
  label: null,
  max: 500,
  min: 0,
  rangeEnd: null,
  step: 1,
  showSteps: false,
  small: false
};
Range.propTypes = {
  /** Disables all inputs */
  disabled: PropTypes__default.bool,
  /** Control the display of value labels */
  displayValues: PropTypes__default.bool,
  /** Injection in className */
  extraClasses: PropTypes__default.string,
  /** Defines element ID that is used to bind it to the labels and validation messages */
  id: PropTypes__default.oneOfType([PropTypes__default.string, PropTypes__default.number]).isRequired,
  /** Provides extra information for this input field */
  info: InputInfoChildren,
  /** Provides label text, required if aria-label is not passed */
  // we want label to be always checked against
  label(_ref2) {
    let {
      'aria-label': ariaLabel,
      label
    } = _ref2;
    // if no aria label nor label throw validation error
    return !ariaLabel && !label ? new Error('Either label or aria label are required') : null;
  },
  /** Format method for display label */
  format: PropTypes__default.func,
  /** Maximum value for the range */
  max: PropTypes__default.number,
  /** Minimum value for the range */
  min: PropTypes__default.number,
  /** Increment/decrement step */
  step: PropTypes__default.number,
  /** Activates range behavior and provides rest props to second input field */
  rangeEnd(_ref3, propName, componentName) {
    let {
      rangeEnd
    } = _ref3;
    // if no aria label throw validation error
    return rangeEnd && !rangeEnd['aria-label'] ? new Error("If you enable ".concat(propName, " on ").concat(componentName, " you must provide an aria-label for it.")) : null;
  },
  /** Control the display of step dots */
  showSteps: PropTypes__default.bool,
  /** Enables small range variation */
  small: PropTypes__default.bool
};
var range = Injecss(Range, styles);

export { Range, range as default };
